import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Checkbox } from 'semantic-ui-react';

import { WLIDS } from 'Constants';
import { store } from 'Models/Store';
import { triggerSendConfirmationEmail } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import alertToast from 'Util/ToastifyUtils';
import { isEmail } from 'Validators/Functions/Email';
import { Flex } from 'Views/Components/Flex/Flex';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { TextInput } from 'Views/Components/New/TextInput';

interface ResendConfirmationEmailModalProps {
	onClose: () => void;
	initialEmail: string;
	bookingIds: string[];
	transactionId?: string;
}

export function ResendConfirmationEmailModal({
	onClose,
	initialEmail,
	bookingIds,
	transactionId,
}: ResendConfirmationEmailModalProps) {
	const [email, setEmail] = useState<string | null>(initialEmail);
	const [emailError, setEmailError] = useState<string>('');
	const [mode, setMode] = useState<'me' | 'other'>('me');

	const onCancel = () => {
		onClose();
	};

	const onConfirm = async () => {
		if (!email) {
			setEmailError('Required');
			return;
		}
		if (!isEmail(email)) {
			setEmailError('Must be valid a email');
			return;
		}
		try {
			await triggerSendConfirmationEmail(bookingIds, email, transactionId);
			alertToast('Confirmation sent', 'success');
		} catch (e) {
			alertToast('Confirmation could not send', 'error');
		}
		onClose();
	};

	const onModeChange = (value: 'me' | 'other') => {
		setMode(value);
		if (value === 'me') {
			// Reset email to me
			setEmail(initialEmail);
			setEmailError('');
		} else {
			setEmail(null);
		}
	};

	const options = getResendOptions(initialEmail);

	return (
		<>
			<NewModal.Header>
				<NewModal.Title>
					<FormattedMessage id={WLIDS.resendBookingConfirmation.title} defaultMessage="Share this ticket" />
				</NewModal.Title>
			</NewModal.Header>
			<NewModal.Body className="resend-confirmation-modal-content">
				<Flex direction="col" gap="sm">
					<div className="refund-bookings__list">
						{options.map(option => {
							return (
								<div
									key={option.value}
									className="booking-refund-item"
									data-test-id={`resend-option-${option.value}`}
								>
									<Checkbox
										label={option.display}
										checked={mode === option.value}
										onChange={() => {
											if (option.value !== 'me' && option.value !== 'other') {
												return;
											}
											onModeChange(option.value);
										}}
									/>
								</div>
							);
						})}
					</div>
					{mode === 'other' && (
						<TextInput
							autoFocus
							required
							type="email"
							label="Email"
							placeholder="email@example.com"
							data-test-id="resend-other-email-input"
							value={email ?? ''}
							onChange={e => {
								setEmail(e.target.value);
								setEmailError('');
							}}
							error={emailError}
						/>
					)}
				</Flex>
			</NewModal.Body>
			<NewModal.Footer>
				<NewModal.CancelButton onClick={onCancel}>Cancel</NewModal.CancelButton>
				<NewModal.ConfirmButton onClick={onConfirm}>
					<FormattedMessage id={WLIDS.resendBookingConfirmation.action} defaultMessage="Send email" />
				</NewModal.ConfirmButton>
			</NewModal.Footer>
		</>
	);
}

export function resendConfirmationEmailModal(bookingIds: string[], initialEmail: string, transactionId?: string) {
	return new Promise<void>((resolve, reject) => {
		const onClose = () => {
			store.modal.hide();
			resolve();
		};

		store.modal?.show(
			'resend confirmation email',
			<ResendConfirmationEmailModal
				onClose={onClose}
				initialEmail={initialEmail}
				bookingIds={bookingIds}
				transactionId={transactionId}
			/>,
		);
	});
}

const getResendOptions = (initialEmail: string) => {
	const bookingOwner = store.isStaff ? initialEmail : 'me';

	return [
		{ display: `Send to ${bookingOwner}`, value: 'me' },
		{ display: 'Send to someone else', value: 'other' },
	];
};
